import { Injectable } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, shareReplay } from 'rxjs';

export enum InspectionViewType {
  STANDARD_LIST = 'standardList',
  STANDARD_SUMMARY = 'standardSummary',
  STANDARD_REPORT = 'standardReport',

  RESIDENT_LIST = 'residentList',
  RESIDENT_SUMMARY = 'residentSummary',
  RESIDENT_REPORT = 'residentReport',

  BULK = 'bulk',
  IN_REVIEW = 'inReview',
  DYNAMIC = 'dynamic',
  REPEATING = 'repeating',
}

@Injectable({
  providedIn: 'root',
})
export class InspectionsViewService {
  #currentView$ = new BehaviorSubject<InspectionViewType>(InspectionViewType.STANDARD_LIST);
  currentView$ = this.#currentView$.pipe(distinctUntilChanged(), shareReplay(1));

  isResident$ = this.currentView$.pipe(
    map(viewType =>
      [
        InspectionViewType.RESIDENT_LIST,
        InspectionViewType.RESIDENT_SUMMARY,
        InspectionViewType.RESIDENT_REPORT,
      ].includes(viewType)
    )
  );

  isReportView$ = this.currentView$.pipe(
    map(viewType => [InspectionViewType.STANDARD_REPORT, InspectionViewType.RESIDENT_REPORT].includes(viewType))
  );

  isSummaryView$ = this.currentView$.pipe(
    map(viewType => [InspectionViewType.STANDARD_SUMMARY, InspectionViewType.RESIDENT_SUMMARY].includes(viewType))
  );

  setViewType(viewType: InspectionViewType) {
    this.#currentView$.next(viewType);
  }

  getCurrentViewType(): InspectionViewType {
    return this.#currentView$.getValue();
  }

  setViewTypeFromRoute(url: string) {
    if (url?.includes('inspectionsView=SUMMARY')) {
      this.setViewType(InspectionViewType.STANDARD_SUMMARY);
      return;
    }
    const urlSegments = this.getCurrentUrlSegments(url);
    const viewType = this.getViewTypeFromRoute(urlSegments);
    this.setViewType(viewType);
  }

  getViewTypeFromRoute(urlSegments: string[]): InspectionViewType {
    const path = urlSegments.join('/');
    if (path.includes('resident')) {
      if (path.includes('resident/report')) return InspectionViewType.RESIDENT_REPORT;
      if (path.includes('resident/summary')) return InspectionViewType.RESIDENT_SUMMARY;
      return InspectionViewType.RESIDENT_LIST;
    }

    if (path.includes('user')) {
      if (path.includes('user/report')) return InspectionViewType.STANDARD_REPORT;
      if (path.includes('user/summary')) return InspectionViewType.STANDARD_SUMMARY;
      return InspectionViewType.STANDARD_LIST;
    }

    if (path.includes('bulk')) return InspectionViewType.BULK;
    if (path.includes('repeating')) return InspectionViewType.REPEATING;
    if (path.includes('dynamic')) return InspectionViewType.DYNAMIC;
    if (path.includes('in-review')) return InspectionViewType.IN_REVIEW;

    return InspectionViewType.STANDARD_LIST;
  }

  getTableTitle(viewType: InspectionViewType): string {
    switch (viewType) {
      case InspectionViewType.STANDARD_LIST:
        return 'Standard';
      case InspectionViewType.RESIDENT_LIST:
        return 'Resident';
      case InspectionViewType.BULK:
        return 'Bulk';
      case InspectionViewType.REPEATING:
        return 'Repeating';
      case InspectionViewType.DYNAMIC:
        return 'Dynamic';
      case InspectionViewType.IN_REVIEW:
        return 'In Review';
      default:
        return 'Standard';
    }
  }

  getTableTooltip(viewType: InspectionViewType): string {
    switch (viewType) {
      case InspectionViewType.STANDARD_LIST:
      case InspectionViewType.RESIDENT_LIST:
        return 'View or create one-time inspections';
      case InspectionViewType.BULK:
        return 'Create bulk inspections';
      case InspectionViewType.REPEATING:
        return 'Create repeating inspections';
      case InspectionViewType.DYNAMIC:
        return 'Automatically create inspections when events occur such as Move Out, Move In, or Notice-to-Vacate';
      case InspectionViewType.IN_REVIEW:
        return '';
      default:
        return '';
    }
  }

  getCurrentUrlSegments(currentUrl: string): string[] {
    const segments = currentUrl.replace(/^\//, '').split('/');
    return segments.filter(segment => segment !== '' && segment !== 'inspections' && !segment.includes(';'));
  }
}
